import * as RSlider from "@radix-ui/react-slider";
import { type ReactElement } from "react";

import { cn } from "../utils/cn";

export interface SliderProps {
  /** Current value(s). Single-element array = single thumb; two-element = range. */
  value: readonly number[];
  onValueChange: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

/** 0–N slider thin enough to live next to a label inside a filter drawer.
 * Renders one thumb per value; two thumbs make it a range slider. Track
 * + range use design-token colors so the OKLCH theming applies. */
export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  ariaLabel,
  className,
}: SliderProps): ReactElement {
  return (
    <RSlider.Root
      className={cn(
        "relative flex h-5 w-full touch-none select-none items-center",
        disabled && "opacity-50",
        className,
      )}
      value={[...value]}
      onValueChange={onValueChange}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <RSlider.Track className="relative h-1.5 grow rounded-full bg-neutral-200">
        <RSlider.Range className="absolute h-full rounded-full bg-primary-500" />
      </RSlider.Track>
      {value.map((_, idx) => (
        <RSlider.Thumb
          key={idx}
          className={cn(
            "block h-4 w-4 rounded-full border-2 border-primary-500 bg-neutral-0 shadow-sm transition-colors",
            "hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
          )}
        />
      ))}
    </RSlider.Root>
  );
}
